import type { PdfCurrencyDisplay } from "@repo/shared/types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { CurrencyDisplayField } from "./currency-display-field";
import { T } from "./portfolio-display-form.i18n";
import type { ComponentFormProps } from "./types";

export type PortfolioDisplayConfig = {
  title?: string;
  description?: string;
  /** QL-414 — absent = the block's default. */
  currencyDisplay?: PdfCurrencyDisplay;
  showWeights: boolean;
  showIsin: boolean;
  showAmount: boolean;
  showUnits?: boolean;
};

type PortfolioDisplayFormProps = ComponentFormProps<PortfolioDisplayConfig>;

export function PortfolioDisplayForm({ config, onChange }: PortfolioDisplayFormProps) {
  const t = useT(T);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="portfolio-title">{t.title}</Label>
        <Input
          id="portfolio-title"
          value={config.title || ""}
          onChange={(e) => onChange({ ...config, title: e.target.value })}
          placeholder={t.titlePlaceholder}
        />
        <p className="text-xs text-muted-foreground">{t.titleHelp}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="portfolio-description">{t.description}</Label>
        <Textarea
          id="portfolio-description"
          value={config.description || ""}
          onChange={(e) => onChange({ ...config, description: e.target.value })}
          placeholder={t.descriptionPlaceholder}
          rows={2}
        />
        <p className="text-xs text-muted-foreground">{t.descriptionHelp}</p>
      </div>

      <CurrencyDisplayField
        blockType="portfolio_display"
        value={config.currencyDisplay}
        onChange={(currencyDisplay) => onChange({ ...config, currencyDisplay })}
      />

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="show-isin" className="text-base">
            {t.showIsin}
          </Label>
          <p className="text-sm text-muted-foreground">{t.showIsinHelp}</p>
        </div>
        <Switch
          id="show-isin"
          checked={config.showIsin !== false}
          onCheckedChange={(checked) => onChange({ ...config, showIsin: checked })}
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="show-weights" className="text-base">
            {t.showWeights}
          </Label>
          <p className="text-sm text-muted-foreground">{t.showWeightsHelp}</p>
        </div>
        <Switch
          id="show-weights"
          checked={config.showWeights !== false}
          onCheckedChange={(checked) => onChange({ ...config, showWeights: checked })}
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="show-amount" className="text-base">
            {t.showAmount}
          </Label>
          <p className="text-sm text-muted-foreground">{t.showAmountHelp}</p>
        </div>
        <Switch
          id="show-amount"
          checked={config.showAmount === true}
          onCheckedChange={(checked) => onChange({ ...config, showAmount: checked })}
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="show-units" className="text-base">
            {t.showUnits}
          </Label>
          <p className="text-sm text-muted-foreground">{t.showUnitsHelp}</p>
        </div>
        <Switch
          id="show-units"
          checked={config.showUnits === true}
          onCheckedChange={(checked) => onChange({ ...config, showUnits: checked })}
        />
      </div>
    </div>
  );
}

export function getDefaultPortfolioDisplayConfig(): PortfolioDisplayConfig {
  return {
    title: "Portfolio",
    description: "Recommended portfolio allocation.",
    showWeights: true,
    showIsin: true,
    showAmount: false,
  };
}
